import { ADMIN_DASHBOARD_COPY } from '@sonar-software/sonar-ts-constants';
import { AsyncStateNotification } from '@ui/notifications';
import { createAsyncSelectors } from '@lib/selectors';
import { FeatureFlagCard } from './FeatureFlagCard';
import {
	FeatureFlagLabel
} from '@sonar-software/sonar-core';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { objectKeys } from '@sonar-software/sonar-ts-lib';
import { SearchInput } from '@ui/general';
import { SonarTable } from '@ui/SonarTable';
import { VerticalColumn } from '@ui/general/VerticalColumn';
import { VerticalColumnView } from '@ui/general/VerticalColumnView';

import { adminActions, getAdminFeatureFlagsAllResponse } from '@sonar-software/sonar-admin';
import { featureFlagColumnData, getFeatureFlagTableColumns } from './FeatureFlagTableColumns';
import React, { useCallback, useMemo, useState } from 'react';
import { useAction, useAsyncState, useOnMount } from '@hooks';

const featureFlagSelectors = createAsyncSelectors((state) => state.admin.featureFlag.get);
const overrideDefaultSelectors = createAsyncSelectors((state) => state.admin.featureFlag.byId.customer.override);
const setDefaultSelectors = createAsyncSelectors((state) => state.admin.featureFlag.byId.customer.useDefault);
const postFeatureFlagSelectors = createAsyncSelectors((state) => state.admin.featureFlag.byId.patch);

const lowercaseFeatureFlags = objectKeys(FeatureFlagLabel).reduce((obj, key) => {
	obj[ key.toLocaleLowerCase() ] = FeatureFlagLabel[ key ];

	return obj;
}, {} as Record<string, string>);

function getData(data?: getAdminFeatureFlagsAllResponse | null): Array<featureFlagColumnData> {
	if (!data || data === null || data.featureFlags.length === 0) {
		return [];
	}

	const orgByGroup = data.featureFlags
		.reduce((hashmap: {[key : string]: featureFlagColumnData}, curr) => {

			let enabledCustomers = 0;

			if (curr.state === true) {
				enabledCustomers = data.totalCustomerCount;
			} else if (curr.state === false) {
				enabledCustomers = 0;
			} else {
				enabledCustomers = curr.enabledByDefault ?
					data.totalCustomerCount - curr.exceptionCustomers
						.filter((x) => x.enabled !== curr.enabledByDefault).length :
					curr.exceptionCustomers.filter((x) => x.enabled).length;
			}


			if (curr.groupId) {
				if (!hashmap[ curr.groupId ]) {
					hashmap[ curr.groupId ] = {
						children: [],
						id: curr.groupId,
						label: curr.groupName ?? ''
					};
				}

				hashmap[ curr.groupId ]?.children?.push({
					count: data.totalCustomerCount === enabledCustomers ? 'All' : enabledCustomers,
					enabledByDefault: curr.enabledByDefault,
					id: curr.name,
					label: lowercaseFeatureFlags[ curr.name.toLowerCase() ] ?? curr.name,
					state: curr.state
				});
			} else {
				hashmap[ curr.name ] = {
					count: data.totalCustomerCount === enabledCustomers ? 'All' : enabledCustomers,
					enabledByDefault: curr.enabledByDefault,
					id: curr.name,
					label: lowercaseFeatureFlags[ curr.name.toLowerCase() ] ?? curr.name,
					state: curr.state
				};
			}

			return hashmap;
		}, {});

	return Object.values(orgByGroup);
}

export const FeatureFlagsView: React.FC = () => {
	const load = useAction(adminActions.requestGetAdminFeatureFlags);
	const [ filter, setFilter ] = useState('');
	const { data, isRequested } = useAsyncState(featureFlagSelectors);
	const [ editRecordId, setEditRecordId ] = useState<string | null>(null);

	const handleOnClick = useCallback((name: string) => {
		setEditRecordId(data?.featureFlags.find((record) => record.name === name)?.name ?? null);
	}, [ data ]);

	const columns = useMemo(() => getFeatureFlagTableColumns(handleOnClick), [ handleOnClick ]);

	const managedData = useMemo(() => getData(data), [ data ]);

	const groupOptions = useMemo(() => managedData
		.filter((record) => Boolean(record.children))
		.map((record) => ({
			label: record.label,
			value: record.id
		})), [ managedData ]);

	const filteredData = useMemo(() => (
		filter === undefined ?
			managedData :
			managedData.reduce((prev, curr) => {
				const lowerCaseFilter = filter.toLowerCase();

				if (curr.label.toLowerCase().includes(lowerCaseFilter)) {
					return [ ...prev, curr ];
				}

				if (curr.children) {
					curr.children = curr.children
						.filter((child) => child.label.toLowerCase().includes(lowerCaseFilter));

					if (curr.children.length > 0) {
						return [ ...prev, curr ];
					}

				}

				return prev;

			}, []))
	, [ managedData, filter ]);

	const editRecord = useMemo(() =>
		data?.featureFlags.find((record) => record.name === editRecordId) ?? null
	, [ data, editRecordId ]);

	useOnMount(() => {
		load();
	});

	return (
		<>
			<VerticalColumnView>
				<VerticalColumn>
					<SearchInput
						onChange={ (value: string) => {
							setFilter(value);
						} } />
					<LoadingSpinner spinning={ isRequested && !data }>
						{ data &&
							<SonarTable
								columns={ columns }
								dataSource={ filteredData }
								expandable={ {
									defaultExpandedRowKeys: [
										...filteredData
											.filter((record) => record.children && record.children.length > 0)
											.map((record) => record.id)
									]
								} }
								id='feature-flags-table'
								pagination={ false }
								rowKey='id'
								scroll={ { x: 'max-content' } } /> }
					</LoadingSpinner>
				</VerticalColumn>
				<VerticalColumn>
					<FeatureFlagCard
						featureFlag={ editRecord }
						groupOptions={ groupOptions } />
				</VerticalColumn>
			</VerticalColumnView>
			<AsyncStateNotification
				errored={ ADMIN_DASHBOARD_COPY.ADD_CUSTOMER_FAILED }
				failed={ ADMIN_DASHBOARD_COPY.ADD_CUSTOMER_FAILED }
				received={ ADMIN_DASHBOARD_COPY.ADD_CUSTOMER_SUCCESS }
				stateSelector={ overrideDefaultSelectors.state } />
			<AsyncStateNotification
				errored={ ADMIN_DASHBOARD_COPY.REMOVE_CUSTOMER_FAILED }
				failed={ ADMIN_DASHBOARD_COPY.REMOVE_CUSTOMER_FAILED }
				received={ ADMIN_DASHBOARD_COPY.REMOVE_CUSTOMER_SUCCESS }
				stateSelector={ setDefaultSelectors.state } />
			<AsyncStateNotification
				errored={ ADMIN_DASHBOARD_COPY.UPDATE_FEATURE_FLAG_FAILED }
				failed={ ADMIN_DASHBOARD_COPY.UPDATE_FEATURE_FLAG_FAILED }
				received={ ADMIN_DASHBOARD_COPY.UPDATE_FEATURE_FLAG_SUCCESS }
				stateSelector={ postFeatureFlagSelectors.state } />
		</>
	);
};
